// Types for parent-vs-children Result Reconciliation.
//
// A parent agent (Ward/LGA/State) uploads their own collation result (Source A).
// The sum of their DIRECT children's uploaded results is Source B. In INEC's
// process these must be equal (a Ward result IS the sum of its polling units).
// When they diverge, the gap is a transcription error or collation-level fraud.
// IReV (INEC's Independent Result Verification) is a partial, leaf-only THIRD
// source used purely as a neutral tiebreaker — it never gates the verdict.
//
// The tool is deliberately NEUTRAL: it surfaces A vs B (and IReV) and pinpoints
// WHERE they diverge; it never asserts which source is "correct".
//
// NOTE: this is a distinct fourth axis of "discrepancy" — keep it separate from
// (1) IResultAnalyticsData.hasConflict (opaque upload "Conflict" badge),
// (2) IAssignmentMeta.hasConflict (disputed assignment), and
// (3) the in-result `mismatch` (Σ party votes ≠ recorded valid votes).

// Parent levels that own a reconciliation (a leaf EC8A polling unit has no
// children, so it is never a reconciliation subject).
export type ReconLevel = 'EC8B' | 'EC8C' | 'EC8D';

// Per-comparison severity band. `awaitingCoverage` is a verdict-only state (it is
// never a per-row severity) used when child coverage is below 100%.
export type ReconSeverity = 'reconciled' | 'minor' | 'critical';
export type ReconVerdict = ReconSeverity | 'awaitingCoverage';

// The seven INEC turnout figures. Canonical keys mirror IChildResultDetail
// (legacy 'pooling' spelling on the ballots field preserved).
export interface IReconTurnout {
	votersOnRegister: number;
	accreditedVoters: number;
	ballotPapersIssuedToPoolingUnit: number;
	unusedBallotPapers: number;
	rejectedBallot: number;
	totalValidVotes: number;
	totalUsedBallotPapers: number;
}

export type ReconTurnoutField = keyof IReconTurnout;

// Human labels for the turnout fields (shared by the engine + the UI).
export const RECON_TURNOUT_LABELS: Record<ReconTurnoutField, string> = {
	votersOnRegister: 'Registered voters',
	accreditedVoters: 'Accredited voters',
	ballotPapersIssuedToPoolingUnit: 'Ballots issued',
	unusedBallotPapers: 'Unused ballots',
	rejectedBallot: 'Rejected ballots',
	totalValidVotes: 'Valid votes',
	totalUsedBallotPapers: 'Total used ballots',
};

export interface IReconSourceParty {
	partyId: string | number;
	partyName: string;
	partyShortName?: string;
	votes: number;
}

// One source's tallied figures (A, B, or the IReV roll-up).
export interface IReconSource {
	turnout: Partial<IReconTurnout>;
	parties: IReconSourceParty[];
	// The party leading this source, for winner-flip display. null when the
	// source has no votes (e.g. Source A not yet uploaded).
	leadingPartyId?: string | number | null;
	leadingPartyName?: string | null;
}

// Direct-child upload coverage. The verdict is `awaitingCoverage` until
// childrenUploaded === childrenTotal (and childrenTotal > 0).
export interface IReconCompleteness {
	childrenTotal: number;
	childrenUploaded: number;
}

// IReV availability across the leaf polling units in this subtree. Always shown
// as a caption ("IReV 9 of 12 units"); never blocks the verdict.
export interface IReconIRevCoverage {
	irevTotal: number;
	irevAvailable: number;
}

// One party's A-vs-B(-vs-IReV) comparison.
export interface IReconPartyDelta {
	partyId: string | number;
	partyName: string;
	partyShortName?: string;
	aVotes: number;
	bVotes: number;
	irevVotes?: number | null;
	// bVotes - aVotes (signed: positive = children report more than the parent).
	delta: number;
	deltaAbs: number;
	// |delta| relative to max(aVotes, bVotes), 0..1.
	deltaPct: number;
	severity: ReconSeverity;
}

// One turnout figure's A-vs-B(-vs-IReV) comparison.
export interface IReconTurnoutDelta {
	field: ReconTurnoutField;
	label: string;
	aValue: number;
	bValue: number;
	irevValue?: number | null;
	delta: number;
	deltaAbs: number;
	deltaPct: number;
	severity: ReconSeverity;
}

export interface IReconWinnerFlip {
	flipped: boolean;
	aLeaderPartyId?: string | number | null;
	aLeaderName?: string | null;
	bLeaderPartyId?: string | number | null;
	bLeaderName?: string | null;
}

// Tolerance thresholds (mirrors the backend `verification-config`). A delta at or
// below `okPct` reconciles; at or below `minorPct` is minor; above is critical.
// A winner flip is always critical regardless of magnitude.
export interface IReconThresholds {
	okPct: number;
	minorPct: number;
	// Absolute floor (votes) below which a delta is always treated as reconciled,
	// so a 1-vote rounding difference never escalates a large tally.
	absFloor: number;
	ruleVersion?: string;
}

export const DEFAULT_RECON_THRESHOLDS: IReconThresholds = {
	okPct: 0, // exact match reconciles
	minorPct: 0.02, // ≤2% divergence = minor
	absFloor: 1, // a single vote never escalates
	ruleVersion: 'client-1',
};

// The full reconciliation for one parent entity.
export interface IReconciliationResult {
	verdict: ReconVerdict;
	level: ReconLevel;
	entityCode: string;
	entityName?: string;
	completeness: IReconCompleteness;
	irevCoverage?: IReconIRevCoverage | null;
	sourceA: IReconSource;
	sourceB: IReconSource;
	irev?: IReconSource | null;
	partyDeltas: IReconPartyDelta[];
	turnoutDeltas: IReconTurnoutDelta[];
	winnerFlip: IReconWinnerFlip;
	ruleVersion?: string;
	// Whether Source A (the parent's own upload) exists yet. Distinct from a
	// genuine "reconciles to zero" — a missing parent result is not a verdict.
	sourceAUploaded: boolean;
	// The agent's persisted follow-up decision (read-back overlay), when one exists.
	decision?: IReconDecision | null;
}

// Discriminated on `reachable` (mirrors the other src/lib outcomes).
export type ReconciliationOutcome =
	| { reachable: false }
	| { reachable: true; httpStatus: number; body: IReconciliationResult | null };

// ────────────────────────────────────────────────────────────
// Detect + escalate: the analyst decision persisted server-side.
// ────────────────────────────────────────────────────────────
export type ReconDecisionState = 'acknowledged' | 'resolved' | 'escalated';

export interface IReconDecision {
	state: ReconDecisionState;
	note?: string;
	reportId?: string | null;
	byUserId?: string;
	at?: string;
}

export interface IReconDecisionPayload {
	electionId: number;
	level: ReconLevel;
	entityCode: string;
	state: ReconDecisionState;
	note?: string;
	reportId?: string | null;
}

export type ReconDecisionOutcome = { reachable: false } | { reachable: true; httpStatus: number; body: IReconDecision | null };
